import React, { useEffect, useRef, useState } from 'react';
import { Badge, Box, Drawer, Fade, List, ListItem, ListItemIcon, Typography, useMediaQuery } from '@mui/material';
import HomeOutlinedIcon from '@mui/icons-material/HomeOutlined';
import NotificationsNoneOutlinedIcon from '@mui/icons-material/NotificationsNoneOutlined';
import LibraryBooksOutlinedIcon from '@mui/icons-material/LibraryBooksOutlined';
import SearchIcon from '@mui/icons-material/Search';
import LogoutOutlinedIcon from '@mui/icons-material/LogoutOutlined';
import SettingsIcon from '@mui/icons-material/Settings';
import { styled } from '@mui/material/styles';
import getTheme from '../../theme/theme';
import { PinnacleThemes } from '../../theme/types';
import { useLocation, useNavigate } from 'react-router-dom';
import { PinnacleDialog } from '../../components/PinnacleDialog';
import AccountProfilePage from '../../pages/AccountProfilePage/AccountProfilePage';
import { APP_SEARCH_BANNER_URL, SETTINGS_CHANGE_PASSWORD, SETTINGS_PROFILE, SETTINGS_TERMS_AND_CONDITIONS } from '../../utils/constants';
import AccountChangePassword from '../../pages/AccountChangePassword/AccountChangePassword';
import SearchPage from '../../pages/SearchPage/SearchPage';
import LogoutDialog from './LogoutDialog/LogoutDialog';
import MobileSidebar from './MobileSidebar';

const SidebarContainer = styled(Box)(({ theme }) => ({
    width: '72px',
    height: '100vh',
    background: 'linear-gradient(to bottom, #0B4176 0%, #010911 40%, #010911 100%)',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    padding: theme.spacing(2, 0),
    boxSizing: 'border-box',
    position: 'fixed',
    left: 0,
    top: 0,
    zIndex: 1200,
}));

const Logo = styled(Typography)(() => ({
    color: '#fff',
    fontWeight: 700,
    fontSize: '1rem',
    marginBottom: '190px',
}));

const MenuList = styled(List)(() => ({
    display: 'flex',
    flexDirection: 'column',
    gap: 0,
    flexGrow: 1,
    width: '100%',
    padding: 0,
}));

const MenuItem = styled(ListItem)(({ theme }) => ({
    justifyContent: 'center',
    width: '100%',
    minHeight: '48px',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    position: 'relative',
    padding: 0,
    margin: '6px 0',
    // Only apply hover color on desktop
    '@media (hover: hover) and (pointer: fine)': {
        '&:hover .MuiListItemIcon-root': {
            color: '#000',
        },
    },
}));

const HoverBar = styled(Box)(({ theme }) => ({
    position: 'fixed',
    left: 0,
    height: '48px',
    backgroundColor: '#FFD700',
    borderRadius: `0 ${theme.shape.borderRadius}px ${theme.shape.borderRadius}px 0`,
    display: 'flex',
    alignItems: 'center',
    zIndex: 1200,
    pointerEvents: 'none',
    transition: 'all 0.3s ease',
    paddingLeft: '25px',
}));

const HoverLabel = styled(Typography)(({ theme }) => ({
    color: '#000',
    fontWeight: 600,
    marginLeft: '30px',
    whiteSpace: 'nowrap',
}));

interface SidebarProps {
    unreadNotification?: boolean;
}
const Sidebar: React.FC<SidebarProps> = ({ unreadNotification }) => {
    const theme = getTheme(PinnacleThemes.BASE_THEME);
    const location = useLocation();
    const navigate = useNavigate();
    // const [showScrollTop, setShowScrollTop] = useState(false);

    const isMobile = useMediaQuery('(max-width:600px)');
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [hoverState, setHoverState] = useState<{ label: string; top: number; icon: React.ReactNode } | null>(null);
    const [show, setShow] = useState(false);
    const [settingsExpanded, setSettingsExpanded] = useState(false);
    const sidebarRef = useRef<HTMLDivElement>(null);
    const [isSettingsDialogBoxOpen, setIsSettingsDialogBoxOpen] = useState<boolean>(false);
    const [dialogBoxCurrentPath, setDialogBoxCurrentPath] = useState<string>('');
    const [isSearchDialogBoxOpen, setIsSearchDialogBoxOpen] = useState<boolean>(false);
    const [searchTermForDialog, setSearchTermForDialog] = useState<string>('');
    const [isLogoutDialogOpen, setIsLogoutDialogOpen] = useState<boolean>(false);

    const menuItems = [
        { icon: <SearchIcon />, path: '/search', label: 'Search', type: 'dialog' },
        {
            icon: (
                unreadNotification ? (
                    <Badge
                        color="error"
                        variant="dot"
                        invisible={false}
                        sx={{
                            '& .MuiBadge-dot': {
                                backgroundColor: theme.palette.secondary.main,
                                right: 3,
                                top: 1
                            },
                        }}
                    >
                        <NotificationsNoneOutlinedIcon />
                    </Badge>
                ) : (
                    <NotificationsNoneOutlinedIcon />
                )
            )
            , path: '/notifications', label: 'Notification', type: 'navigate'
        },
        { icon: <HomeOutlinedIcon />, path: '/', label: 'Home', type: 'navigate' },
        { icon: <LibraryBooksOutlinedIcon />, path: '/references', label: 'References', type: 'navigate', activePaths: ['/references', '/my-favorite-references'] },
        {
            icon: <SettingsIcon />,
            path: '/account-settings',
            label: 'Settings',
            type: 'subItems',
            subItems: [
                { label: 'Profile', path: 'Profile' },
                { label: 'Change Password', path: 'Change Password' },
                { label: 'Terms & Services', path: 'Terms and Conditions' },
            ],
            activePaths: ['/terms']
        },
        { icon: <LogoutOutlinedIcon />, path: '/logout', label: 'Logout' },
    ];

    useEffect(() => {
        const lastSearchTerm = sessionStorage.getItem('lastSearchTerm') || '';
        const wasSearchDialogOpen = sessionStorage.getItem('searchDialogOpen') === 'true';

        if (wasSearchDialogOpen) {
            setSearchTermForDialog(lastSearchTerm);
            setIsSearchDialogBoxOpen(true);
            // Save the current path for back navigation
            setDialogBoxCurrentPath(location.pathname);
        }

        const handleClickOutside = (event: MouseEvent) => {
            if (
                sidebarRef.current &&
                !sidebarRef.current.contains(event.target as Node)
            ) {
                setSettingsExpanded(false);
            }
        };

        const handleScroll = () => {
            // setShowScrollTop(window.scrollY > 300);
        };

        document.addEventListener('mousedown', handleClickOutside);
        window.addEventListener('scroll', handleScroll);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
            window.removeEventListener('scroll', handleScroll);
        };

    }, [location.pathname]);

    // const scrollToTop = () => {
    //     window.scrollTo({ top: 0, behavior: 'smooth' });
    // };

    const handleMouseEnter = (item: typeof menuItems[0], event: React.MouseEvent) => {
        const rect = event.currentTarget.getBoundingClientRect();
        if (show && hoverState?.label !== item.label) {
            setShow(false);
            setTimeout(() => {
                setHoverState({ label: item.label, top: rect.top, icon: item.icon });
                setShow(true);
            }, 200);
        } else {
            setHoverState({ label: item.label, top: rect.top, icon: item.icon });
            setShow(true);
        }
    };

    const handleMouseLeave = () => {
        setShow(false);
    };

    const handleSettingsDialog = (path: string) => {
        if (path === SETTINGS_TERMS_AND_CONDITIONS) {
            navigate('/terms');
        } else {
            setIsSettingsDialogBoxOpen(true);
            setDialogBoxCurrentPath(path);
        }
    }

    const menuItemsContent = (
        <>
            {menuItems.map((item, idx) => {
                const isActive = item.activePaths
                    ? item.activePaths.includes(location.pathname)
                    : location.pathname === item.path;

                return (
                    <React.Fragment key={idx}>
                        <MenuItem
                            onClick={() => {
                                if (item.type === 'subItems' && item.label === 'Settings') {
                                    setSettingsExpanded(!settingsExpanded);
                                } else if (item.label === 'Logout') {
                                    setIsLogoutDialogOpen(true);
                                    setSettingsExpanded(false);
                                } else if (item.type === 'dialog') {
                                    if (item.label === 'Search') {
                                        // save current path
                                        setDialogBoxCurrentPath(location.pathname);

                                        // optionally save last search term from previous search
                                        const lastSearchTerm = sessionStorage.getItem('lastSearchTerm') || '';
                                        setSearchTermForDialog(lastSearchTerm);

                                        setIsSearchDialogBoxOpen(true);
                                    }
                                    setSettingsExpanded(false);
                                } else {
                                    navigate(item.path);
                                    setSettingsExpanded(false);
                                }
                            }}
                            onMouseEnter={(e) => handleMouseEnter(item, e)}
                            onMouseLeave={handleMouseLeave}
                        >
                            <Box
                                sx={{
                                    backgroundColor: isActive ? theme.palette.secondary.main : 'transparent',
                                    borderRadius: `${theme.shape.borderRadius}px`,
                                    padding: '8px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    transition: 'background-color 0.2s ease',
                                }}
                            >
                                <ListItemIcon
                                    sx={{
                                        color: isActive ? theme.palette.common.black : theme.palette.common.white,
                                        minWidth: 0,
                                        justifyContent: 'center',
                                    }}
                                >
                                    {item.icon}
                                </ListItemIcon>
                            </Box>
                        </MenuItem>

                        {
                            item.label === 'Settings' && settingsExpanded && item.subItems && (
                                <Fade in={settingsExpanded} timeout={300} unmountOnExit>
                                    <List
                                        component="div"
                                        disablePadding
                                        sx={{
                                            position: 'absolute',
                                            left: '16px',
                                            top: `${59 * (idx + 1)}px`,
                                            width: '180px',
                                            backgroundColor: 'rgba(4, 27, 49, 1)',
                                            boxShadow: '2px 2px 15px 3px rgba(143, 143, 143, 0.1)',
                                            borderRadius: `0 ${theme.shape.borderRadius}px ${theme.shape.borderRadius}px 0`,
                                            padding: '4px 0',
                                            zIndex: 1300,
                                        }}
                                    >
                                        {item.subItems.map((subItem, subIdx) => (
                                            <ListItem
                                                key={subIdx}
                                                sx={{
                                                    cursor: 'pointer',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    padding: '8px 16px',
                                                    backgroundColor:
                                                        location.pathname === subItem.path ? '#FFD700' : 'transparent',
                                                    '&:hover': {
                                                        backgroundColor: '#003367',
                                                    },
                                                }}
                                                onClick={() => {
                                                    handleSettingsDialog(subItem.path);
                                                    setSettingsExpanded(false);
                                                }}
                                            >
                                                <Typography
                                                    className="subItemText"
                                                    sx={{
                                                        color:
                                                            location.pathname === subItem.path
                                                                ? theme.palette.common.black
                                                                : theme.palette.common.white,
                                                        fontSize: '0.875rem',
                                                        fontWeight: 500,
                                                        whiteSpace: 'nowrap',
                                                    }}
                                                >
                                                    {subItem.label}
                                                </Typography>
                                            </ListItem>
                                        ))}
                                    </List>
                                </Fade>
                            )
                        }
                    </React.Fragment>
                )
            }
            )}
        </>
    );
    return (
        <>
            {/* Sidebar for desktop */}
            {!isMobile && (
                <SidebarContainer ref={sidebarRef}>
                    <Logo> </Logo>
                    <MenuList>
                        {menuItemsContent}
                    </MenuList>
                </SidebarContainer>
            )}

            {/* Sidebar in Drawer for mobile */}
            {isMobile && (
                <>
                    <Drawer open={drawerOpen} onClose={() => setDrawerOpen(false)}>
                        <SidebarContainer ref={sidebarRef}>
                            <Logo> </Logo>
                            <MenuList>
                                {menuItemsContent}
                            </MenuList>
                        </SidebarContainer>
                    </Drawer>
                </>
            )}


            {/* Mobile menu button */}
            {isMobile && !drawerOpen && (
                <>
                    <MobileSidebar
                        setIsSearchDialogBoxOpen={setIsSearchDialogBoxOpen}
                        setIsLogoutDialogOpen={setIsLogoutDialogOpen}
                        setIsSettingsDialogBoxOpen={setIsSettingsDialogBoxOpen}
                        setDialogBoxCurrentPath={setDialogBoxCurrentPath}
                        setSearchTermForDialog={setSearchTermForDialog}
                        unreadNotification={unreadNotification}
                    />
                </>
            )}

            {/* Hover state */}
            {!isMobile && hoverState && (
                <HoverBar
                    sx={{
                        top: hoverState.top,
                        width: show ? '182px' : '72px',
                        opacity: show ? 1 : 0,
                    }}
                >
                    <Box sx={{ color: '#000', display: 'flex', alignItems: 'center' }}>
                        {hoverState.icon}
                    </Box>
                    <HoverLabel>{hoverState.label}</HoverLabel>
                </HoverBar>
            )}

            {/* Settings dialog box */}
            <PinnacleDialog
                isOpen={isSettingsDialogBoxOpen}
                setIsOpen={setIsSettingsDialogBoxOpen}
                disableBackDropClick
                headerTitle={dialogBoxCurrentPath}
                renderDefaultContent={true}
            >
                {dialogBoxCurrentPath === SETTINGS_PROFILE && (
                    <AccountProfilePage setIsOpen={setIsSettingsDialogBoxOpen} />
                )}

                {dialogBoxCurrentPath === SETTINGS_CHANGE_PASSWORD && (
                    <AccountChangePassword setIsOpen={setIsSettingsDialogBoxOpen} />
                )}

            </PinnacleDialog>

            {/* Search dialog box */}
            <PinnacleDialog
                isOpen={isSearchDialogBoxOpen}
                setIsOpen={setIsSearchDialogBoxOpen}
                disableBackDropClick
                renderShowAllVideosPerSubject={true}
                includeBannerImg={true}
                persistKey="searchDialogOpen"
            >
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        width: '100%',
                        height: '100%',
                    }}
                >
                    {/* Search form centered absolutely */}
                    <Box
                        sx={{
                            flex: 1,
                            display: 'flex',
                            alignItems: 'flex-start',
                            justifyContent: 'center',
                            flexFlow: 'column',
                            width: '100%',
                            maxWidth: 836,
                            marginTop: '-70px',
                            marginBottom: '100px'
                        }}
                    >
                        <SearchPage
                            isOpen={isSearchDialogBoxOpen}
                            setIsOpen={setIsSearchDialogBoxOpen}
                            initialSearchTerm={searchTermForDialog}
                        />
                    </Box>
                </Box>


            </PinnacleDialog>

            {/* Logout Dialog */}
            <LogoutDialog open={isLogoutDialogOpen} setOpen={setIsLogoutDialogOpen} />

            {/* {showScrollTop && (
                <IconButton
                    onClick={scrollToTop}
                    sx={{
                        position: 'fixed',
                        bottom: 32,
                        right: 32,
                        zIndex: 1500,
                        backgroundColor: '#FFD700',
                        color: '#000',
                        '&:hover': {
                            backgroundColor: '#FFC107',
                        },
                        boxShadow: '0px 4px 12px rgba(0,0,0,0.3)',
                    }}
                >
                    <KeyboardArrowUpIcon />
                </IconButton>
            )} */}
        </>
    );
};

export default Sidebar;
